import { dataURLToFile } from "@excalidraw/excalidraw/data/blob";

import { IMAGE_MIME_TYPES, MIME_TYPES } from "@excalidraw/common";

import type { FileId } from "@excalidraw/element/types";

import type { BinaryFileData, DataURL } from "@excalidraw/excalidraw/types";

const BACKEND_URL =
  import.meta.env.VITE_APP_BACKEND_URL ||
  (import.meta.env.DEV ? "http://localhost:9503" : "");
const FILES_API_BASE = BACKEND_URL ? `${BACKEND_URL}/files` : "/api/files";

const IMAGE_MIME_TYPES_SET = new Set(Object.values(IMAGE_MIME_TYPES));

const getRuntimeAccessToken = () => {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  return (
    params.get("accessToken") ||
    params.get("templateAccessToken") ||
    params.get("adminToken") ||
    ""
  );
};

export const saveFilesToBackend = async ({
  files,
}: {
  files: Map<FileId, BinaryFileData>;
}) => {
  const savedFiles = new Map<FileId, BinaryFileData>();
  const erroredFiles = new Map<FileId, BinaryFileData>();

  for (const [id, fileData] of files) {
    try {
      const file = dataURLToFile(fileData.dataURL as DataURL, id);

      const uploadUrl = `${FILES_API_BASE}/${id}`;

      const uploadRes = await fetch(uploadUrl, {
        method: "POST",
        body: file,
        headers: {
          "Content-Type": "application/octet-stream",
          "X-File-Mime-Type":
            fileData.mimeType || file.type || "application/octet-stream",
          ...(getRuntimeAccessToken()
            ? { Authorization: `Bearer ${getRuntimeAccessToken()}` }
            : {}),
        },
      });

      if (!uploadRes.ok) {
        throw new Error(`Failed to upload file ${id}: ${uploadRes.status}`);
      }

      savedFiles.set(id, fileData);
    } catch (error) {
      erroredFiles.set(id, fileData);
    }
  }

  return { savedFiles, erroredFiles };
};

export const loadFilesFromBackend = async (fileIds: string[]) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  await Promise.all(
    fileIds.map(async (id) => {
      try {
        const downloadUrl = `${FILES_API_BASE}/${id}`;

        const response = await fetch(downloadUrl, {
          headers: getRuntimeAccessToken()
            ? { Authorization: `Bearer ${getRuntimeAccessToken()}` }
            : undefined,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch file ${id}: ${response.status}`);
        }

        const blob = await response.blob();
        const mimeType =
          response.headers.get("Content-Type") ||
          blob.type ||
          "application/octet-stream";
        const normalizedMimeType: BinaryFileData["mimeType"] =
          IMAGE_MIME_TYPES_SET.has(
            mimeType as typeof IMAGE_MIME_TYPES[keyof typeof IMAGE_MIME_TYPES],
          )
            ? (mimeType as typeof IMAGE_MIME_TYPES[keyof typeof IMAGE_MIME_TYPES])
            : MIME_TYPES.binary;

        const dataURL = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        loadedFiles.push({
          id: id as FileId,
          dataURL: dataURL as DataURL,
          mimeType: normalizedMimeType,
          created: Date.now(),
          lastRetrieved: Date.now(),
        });
      } catch (error) {
        erroredFiles.set(id as FileId, true);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};
